import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch, useSelector } from 'react-redux';

import BaseMultipleSelectorV2, { BaseMultipleSelectorWithPrompt } from 'components/base/BaseMultipleSelectorV2';
import BasePrompt from 'components/base/prompt/BasePrompt';
import { PermissionCodes } from 'config/PermissionCodes';


import '../campaign/campaignCreation/PropertiesGeneralSection.scss';
import './RewardSection.scss';
import { createAction } from 'utils';
import { errorMessage, hasError } from 'components/base/ErrorFieldMessage';
import { DISPLAY_TARGET_CUSTOMER_TYPE, SEGMENT_TYPE, TARGET_CUSTOMER_TYPE } from 'config/CustomEnums';
import ReactHookFormTitleWithRadio from 'components/reactHookFormComponents/ReactHookFormTitleWithRadio';
import './TargetTypeCustomerSelector.scss'

const mapPropsToState = (state) => ({
  segments: state.segments.notPagedAllList,
  levels: state.levels.levelList,
});
const TargetTypeCustomerSelector = ({ segments, levels, disabled = false }) => {
  const { watch, setValue, formState: { errors } } = useFormContext();
  const dispatch = useDispatch();
  const targetCustomerTypeFieldName = "targetCustomerType";
  const dynamicSegmentFieldName = "dynamicSegments";
  const staticSegmentFieldName = "staticSegments";
  const selectedLevelsFiledName = "targetedLevels";

  const targetCustomerType = watch(targetCustomerTypeFieldName) || [];
  const selectedDynamicSegments = watch(dynamicSegmentFieldName) || [];
  const selectedStaticSegments = watch(staticSegmentFieldName) || [];
  const selectedLevels = watch(selectedLevelsFiledName) || [];

  const selectorConfig = {
    [TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT]: {
      title: DISPLAY_TARGET_CUSTOMER_TYPE?.[TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT],
      namespace: 'segments',
      data: segments,
      selectedList: selectedDynamicSegments,
      setValue: (option) => {
        setValue(dynamicSegmentFieldName, option, { shouldDirty: true })
      },
      addButton: {
        title: 'Add Segments',
      },
      requires: PermissionCodes.addSegment,
      errorIdKey: 'segments',
      defaultFilter: {
        moreSearch: {
          type: SEGMENT_TYPE.DYNAMIC,
          isBlacklist: false,
        },
      },
      promptConfig: {
        title: "Go to create segment?",
        description: "You will leave earning rule creation process.",
        rightButton: {
          text: 'Go to Create Segment',
          action: () => {
            window.open('/segments/create', '_blank');
          }
        }
      },
      loadSourceDataConfig: {
        action: "segments/getAllList",
        filter: {
          status: true,
          moreSearch: {
            type: SEGMENT_TYPE.DYNAMIC,
            isBlacklist: false,
          },
        },
      },
    },
    [TARGET_CUSTOMER_TYPE.STATIC_SEGMENT]: {
      title: DISPLAY_TARGET_CUSTOMER_TYPE?.[TARGET_CUSTOMER_TYPE.STATIC_SEGMENT],
      namespace: 'segments',
      data: segments,
      selectedList: selectedStaticSegments,
      setValue: (option) => {
        setValue(staticSegmentFieldName, option, { shouldDirty: true })
      },
      addButton: {
        title: 'Add Segments',
      },
      requires: PermissionCodes.addSegment,
      errorIdKey: 'segments',
      promptConfig: {
        title: "Go to create segment?",
        description: "You will leave earning rule creation process.",
        rightButton: {
          text: 'Go to Create Segment',
          action: () => {
            window.open('/segments/create', '_blank');
          }
        }
      },
      loadSourceDataConfig: {
        action: "segments/getAllList",
        filter: {
          status: true,
          moreSearch: {
            type: SEGMENT_TYPE.STATIC,
            isBlacklist: false,
          },
        },
      },
    },
    [TARGET_CUSTOMER_TYPE.LEVEL]: {
      title: DISPLAY_TARGET_CUSTOMER_TYPE?.[TARGET_CUSTOMER_TYPE.LEVEL],
      namespace: 'levels',
      data: levels,
      selectedList: selectedLevels,
      setValue: (option) => {
        setValue(selectedLevelsFiledName, option, { shouldDirty: true })
      },
      errorIdKey: selectedLevelsFiledName,
      loadSourceDataConfig: {
        action: "levels/getLevelList",
        filter: {},
      },
    },
  }

  const selectorTypeOptions = [
    TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT,
    TARGET_CUSTOMER_TYPE.STATIC_SEGMENT,
    TARGET_CUSTOMER_TYPE.LEVEL,
  ]?.map(type => ({
    label: DISPLAY_TARGET_CUSTOMER_TYPE?.[type] || type,
    value: type,
  }))

  const getSelectorWithConfig = (config) => {
    return <BaseMultipleSelectorWithPrompt
      title={config?.title}
      namespace={config?.namespace}
      data={{
        sourceData: config?.data,
        targetData: config?.selectedList,
        targetChange: (value) => {
          config?.setValue && config.setValue(value);
        },
      }}
      addButton={config?.addButton}
      error={{
        error: hasError(errors, config?.errorIdKey),
        id: config?.errorIdKey,
        message: errorMessage(errors, config?.errorIdKey),
      }}
      requires={config?.requires}
      groupKey={config?.groupKey}
      promptConfig={config?.promptConfig}
      loadSourceDataConfig={config?.loadSourceDataConfig}
      disabled={disabled}
    />
  }

  useEffect(() => {
    if (
      [
        TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT,
        TARGET_CUSTOMER_TYPE.STATIC_SEGMENT,
      ].includes(targetCustomerType)
    ) {
      const loadSourceDataConfig = selectorConfig?.[targetCustomerType]?.loadSourceDataConfig;
      if (!loadSourceDataConfig?.action) {
        return;
      }
      dispatch(createAction(loadSourceDataConfig?.action)(loadSourceDataConfig?.filter))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCustomerType])

  return (<>
    <div>
      <ReactHookFormTitleWithRadio
        title={"Target customer"}
        defaultValue={TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT}
        options={selectorTypeOptions}
        valueFieldName={targetCustomerTypeFieldName}
        disabled={disabled}
      />
      {getSelectorWithConfig(selectorConfig?.[targetCustomerType])}
    </div>
  </>)
}
export default connect(mapPropsToState)(TargetTypeCustomerSelector);

export const StoreMultipleSelector = ({
  title = "Limited to store (optional)",
  valueFieldName
}) => {
  const dispatch = useDispatch();
  const { pureStores } = useSelector((state) => ({
    pureStores: state.storeModel.pureStores,
  }));
  const { watch, setValue } = useFormContext();
  const watchFieldValue = watch(valueFieldName);

  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [oldSelectedStores, setOldSelectedStores] = useState([]);
  const [pageOfLoadData, setPageOfLoadData] = useState(1);
  const [refreshDate, setRefreshDate] = useState(new Date());
  useEffect(() => {
    dispatch({ type: 'storeModel/getAllPureStores'})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, refreshDate]);
  useEffect(() => {
    if (oldSelectedStores?.length < 1 && watchFieldValue?.length > 0) {
      setOldSelectedStores(watchFieldValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFieldValue])

  return (<>
    <BaseMultipleSelectorV2
      title={title}
      namespace='storeModel'
      data={{
        sourceData: pureStores,
        targetData: watchFieldValue,
        targetChange: (value) => {
          setValue(valueFieldName, value, { shouldDirty: true })
        },
      }}
      custom={{
        customItem: item => item.displayStoreName
      }}
      addButton={{
        title: 'Add Store',
        action: () => {
          setShowCreatePrompt(true);
        },
        requires: PermissionCodes.addStore,
      }}
      requires={PermissionCodes.addStore}
    />
    <BasePrompt
      show={showCreatePrompt}
      closeAction={() => setShowCreatePrompt(false)}
      leftButton={{
        text: 'Done and refresh',
        action: () => {
          setRefreshDate(new Date());
          setPageOfLoadData(1);
          setShowCreatePrompt(false);
        },
      }}
      rightButton={{
        text: 'create store',
        action: () => {
          window.open('/stores/create', '_blank');
        }
      }}
      title={'Go to create store?'}
      description={'Go to create store'}
    />
  </>
  )
}

export const SkuMultipleSelectorWithInputArea = ({
  selectFieldName,
  inputFieldName,
}) => {
  const { productSkus } = useSelector((state) => ({
    productSkus: state.shopify.productSkus,
  }));
  const { watch, setValue } = useFormContext();
  const dispatch = useDispatch()

  const watchSelectedFieldValue = watch(selectFieldName);
  const watchInputFieldValue = watch(inputFieldName);
  const camma = ',';

  useEffect(() => {
    dispatch(createAction('shopify/getProductList')());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (<>
    <BaseMultipleSelectorV2
      title={'SKU (optional)'}
      data={{
        sourceData: productSkus,
        targetData: watchSelectedFieldValue,
        targetChange: (value) => {
          setValue(selectFieldName, value, { shouldDirty: true })
        },
      }}
    />
    <div>
      <label className="tips-message">Please enter the SKU in below box, separate by comma. e.g. sku001, sku002</label>
      <textarea
        onChange={({ target }) => {
          const currentSkus = target.value.trim().split(camma);
          setValue(inputFieldName, currentSkus, { shouldDirty: true })
        }}
        className="custom-textarea-input-box custom-textarea-input-font"
        onFocus={() => { }}
        value={watchInputFieldValue?.join(camma)}
      />
    </div>
  </>)
}